/**
 * Secure Storage — obfuscated localStorage wrapper.
 *
 * Uses XOR + base64 to prevent trivial plaintext read of sensitive values
 * (telegram_id, ref_code). NOT encryption — defense-in-depth against
 * casual localStorage inspection or extension scraping.
 *
 * Auto-migrates legacy plaintext values on first read.
 */

const OBFUSCATION_PREFIX = '__ff:';

// Deterministic key derived from the storage key name itself
function deriveKeyByte(storageKey: string): number {
  let hash = 0;
  for (let i = 0; i < storageKey.length; i++) {
    hash = ((hash << 5) - hash + storageKey.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 256;
}

function xorObfuscate(value: string, keyByte: number): string {
  const bytes = new TextEncoder().encode(value);
  const result = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) {
    result[i] = bytes[i] ^ ((keyByte + i) % 256);
  }
  return btoa(String.fromCharCode(...result));
}

function xorDeobfuscate(encoded: string, keyByte: number): string {
  const raw = atob(encoded);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    bytes[i] = raw.charCodeAt(i) ^ ((keyByte + i) % 256);
  }
  return new TextDecoder().decode(bytes);
}

/**
 * Store an obfuscated value in localStorage.
 */
export function setSecure(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  const keyByte = deriveKeyByte(key);
  const obfuscated = OBFUSCATION_PREFIX + xorObfuscate(value, keyByte);
  localStorage.setItem(key, obfuscated);
}

/**
 * Read a value from localStorage.
 * If the value is legacy plaintext (no prefix), auto-migrates to obfuscated format.
 */
export function getSecure(key: string): string | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(key);
  if (raw === null) return null;

  if (raw.startsWith(OBFUSCATION_PREFIX)) {
    const encoded = raw.slice(OBFUSCATION_PREFIX.length);
    const keyByte = deriveKeyByte(key);
    return xorDeobfuscate(encoded, keyByte);
  }

  // Legacy plaintext — auto-migrate
  setSecure(key, raw);
  return raw;
}

/**
 * Remove a key from localStorage.
 */
export function removeSecure(key: string): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(key);
}

/** All keys that should be obfuscated */
const SENSITIVE_KEYS = [
  'frenflow_telegram_id',
  'frenflow_ref_code',
];

/**
 * Migrate any legacy plaintext values to obfuscated format.
 * Call once on app initialization to ensure no sensitive data
 * remains in plaintext (getSecure auto-migrates on read).
 */
export function migrateSecureStorage(): void {
  if (typeof window === 'undefined') return;
  for (const key of SENSITIVE_KEYS) {
    getSecure(key); // triggers auto-migration if plaintext
  }
}
